import { DOCUMENT } from '@angular/common';
import { computed, DestroyRef, inject, Injectable, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { catchError, EMPTY, filter, from, fromEvent, of, switchMap, tap } from 'rxjs';
import { MenuSheetResult } from '../../../_components/menu-sheet/menu-sheet.model';
import { ParkingPointActionsSheetComponent } from '../../../_components/parking-point-actions-sheet/parking-point-actions-sheet.component';
import { ParkingPointActionsSheetResult } from '../../../_components/parking-point-actions-sheet/parking-point-actions-sheet.type';
import { GeocodeApiService } from '../../../_services/_api/geocode-api.service';
import { ParkingEditLocationApiService } from '../../../_services/_api/parking-edit-location-api.service';
import { ParkingsApiService } from '../../../_services/_api/parkings-api.service';
import { ReviewsApiService } from '../../../_services/_api/reviews-api.service';
import { SharedUtilsService } from '../../../_services/_core/shared-utils.service';
import { ParkingPoint } from '../../../_types/parking-point.type';
import { ReviewSaveData } from '../../../_types/review.type';
import { GuideService } from '../../../guide/guide.service';
import {
  addingPoiConfirmSheetConfig,
  changingPoiPositionOptionsSheetConfig,
} from '../_components/map-ui-overlay/_data/poi-controller-sheet-configs.data';

import { MapService } from './map/map.service';

const LAST_NAVIGATED_PARKING_ID = 'par_last_navigated_parking_id';
const LAST_NAVIGATED_TIMESTAMP = 'par_last_navigated_timestamp';

@Injectable()
export class MapPoisControllerService {
  private readonly _mapService = inject(MapService);
  private readonly _parkingEditLocationApiService = inject(ParkingEditLocationApiService);
  private readonly _sharedUtilsService = inject(SharedUtilsService);
  private readonly _matDialog = inject(MatDialog);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _parkingsApiService = inject(ParkingsApiService);
  private readonly _geocodeApiService = inject(GeocodeApiService);
  private readonly _reviewsApiService = inject(ReviewsApiService);
  private readonly _document = inject(DOCUMENT);
  private readonly _guideService = inject(GuideService);

  private readonly _mockParkingPoint = new ParkingPoint({
    id: -1,
    location: { lat: 52.2297, lng: 21.0122 },
    likeCount: 12,
    dislikeCount: 2,
    address: 'ul. Przykładowa 5, Warszawa',
  });

  constructor() {
    this._initNavigationTracker();
    this._initGuideMockSheet();
  }

  private readonly _isMapLoaded = this._mapService.isMapLoaded;

  // Ma pozostać computed. Do zmiany jest osobna metoda
  private readonly _selectedParking = computed(() => {
    const selectedParkingId = this._mapService.selectedParkingId();
    const parkings = this._parkingsApiService.getParkings()();
    if (selectedParkingId === null) return null;
    return parkings.find((p) => p.id === selectedParkingId) ?? null;
  });

  private readonly _selectedParkingId = this._mapService.selectedParkingId;

  private _initGuideMockSheet(): void {
    let currentSheetDismiss: (() => void) | null = null;

    this._guideService.openMockSheet$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      const sheetRef = this._sharedUtilsService.openSheet(
        ParkingPointActionsSheetComponent,
        { parkingPoint: this._mockParkingPoint },
        { hasBackdrop: true },
      );

      currentSheetDismiss = () => sheetRef.dismiss();

      // If user manually closes the sheet (X or swipe), stop the tour
      sheetRef.onDismiss.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
        currentSheetDismiss = null;
        if (this._guideService.isActive()) {
          this._guideService.stop();
        }
      });
    });

    // Close sheet when service signals we left the sheet zone
    this._guideService.closeMockSheet$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      currentSheetDismiss?.();
      currentSheetDismiss = null;
    });
  }

  listenForSelectedPoiToStartEdit() {
    if (this._isMapLoaded() && this._selectedParkingId()) {
      untracked(() => this.openDefaultPoiMenu());
    }
  }

  openDefaultPoiMenu() {
    this._mapService.removeMoveableMarker();
    this._mapService.flyToPoi(this._selectedParking()!.location, 'CLOSE_ZOOM');

    const parking = this._selectedParking()!;

    const sheetRef = this._sharedUtilsService.openSheet(
      ParkingPointActionsSheetComponent,
      {
        parkingPoint: parking,
      },
      {
        hasBackdrop: true,
      },
    );

    sheetRef.onDismiss
      .pipe(
        switchMap((result) => {
          switch (result) {
            case ParkingPointActionsSheetResult.NAVIGATE:
              this._handleNavigate();
              break;
            case ParkingPointActionsSheetResult.VIEW_REVIEWS:
              sheetRef.dismiss();
              return this._handleViewReviews().pipe(tap(() => this.openDefaultPoiMenu()));
            case ParkingPointActionsSheetResult.UPDATE_LOCATION:
              sheetRef.dismiss();
              return from(this.handleUpdateUserChoice()).pipe(
                switchMap((obs) => obs),
                tap(() => this.openDefaultPoiMenu()),
              );
          }

          sheetRef.dismiss();
          this._selectedParkingId.set(null);
          return of(null);
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  private _handleNavigate() {
    const { id, location } = this._selectedParking() ?? {};
    if (id) {
      localStorage.setItem(LAST_NAVIGATED_PARKING_ID, id.toString());
      localStorage.setItem(LAST_NAVIGATED_TIMESTAMP, Date.now().toString());
    }
    setTimeout(
      () =>
        location &&
        window.open(
          `https://www.google.com/maps/dir/?api=1&destination=${location.lat},${location.lng}`,
          '_blank',
        ),

      100,
    );
  }

  private _initNavigationTracker() {
    this._checkAndPromptForReview();

    fromEvent(this._document, 'visibilitychange')
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        if (this._document.visibilityState === 'visible') {
          this._checkAndPromptForReview();
        }
      });
  }

  private _checkAndPromptForReview() {
    const lastNavigatedId = localStorage.getItem(LAST_NAVIGATED_PARKING_ID);
    const lastNavigatedTimestamp = localStorage.getItem(LAST_NAVIGATED_TIMESTAMP);

    if (!lastNavigatedId) return;

    if (lastNavigatedTimestamp) {
      const now = Date.now();
      const diff = now - parseInt(lastNavigatedTimestamp, 10);

      // Jeżeli od kliknięcia "Nawiguj" minęło mniej niż 10 sekund,
      // to prawdopodobnie właśnie wychodzimy z aplikacji, a nie wracamy.
      if (diff < 10000) return;
    }

    localStorage.removeItem(LAST_NAVIGATED_PARKING_ID);
    localStorage.removeItem(LAST_NAVIGATED_TIMESTAMP);

    const parkingId = parseInt(lastNavigatedId, 10);

    this._parkingsApiService
      .getParking(parkingId)
      .pipe(
        switchMap((parking) => this._handleAddReview(parking, true)),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  private _handleAddReview(
    parking: ParkingPoint | null,
    isReviewForLastNavigatedParking = false,
    skipVoteStep = false,
  ) {
    if (!parking) return of(null);

    return from(import('../_components/add-review/add-review.component')).pipe(
      switchMap(({ AddReviewComponent }) =>
        this._matDialog
          .open(AddReviewComponent, {
            data: {
              parkingPointId: parking.id,
              parkingAddress: parking.address,
              isReviewForLastNavigatedParking,
              skipVoteStep,
            },
          })
          .afterClosed(),
      ),
      switchMap((reviewData: ReviewSaveData | undefined) => {
        if (!reviewData) return of(null);

        return this._reviewsApiService.postReview(parking.id, reviewData).pipe(
          tap(() => {
            this._sharedUtilsService.openSnackbar('Dzięki za opinię!', 'SUCCESS');
          }),
          catchError(() => {
            this._sharedUtilsService.openSnackbar(
              'Wystąpił błąd podczas dodawania opinii',
              'ERROR',
            );
            return of(null);
          }),
        );
      }),
    );
  }

  private _handleViewReviews() {
    return from(import('../_components/reviews/reviews.component')).pipe(
      switchMap(({ ReviewsComponent }) =>
        this._matDialog
          .open(ReviewsComponent, {
            data: { parkingPoint: this._selectedParking() },
          })
          .afterClosed(),
      ),
    );
  }

  async handleUpdateUserChoice() {
    this._mapService.renderMoveableMarkerWithRadiusAndLineToFixedPoint(
      this._selectedParking()!.location,
    );
    this._mapService.jumpToPoi(this._selectedParking()!.location, 'CLOSE_ZOOM');

    const sheetRef = await this._sharedUtilsService.openMenuSheet(
      changingPoiPositionOptionsSheetConfig(),
      {
        hasBackdrop: false,
      },
    );

    return sheetRef.onDismiss.pipe(
      switchMap((result) => {
        if (result === MenuSheetResult.CONFIRM) {
          if (this._mapService.isMarkerInRadiusOfOtherParking()) {
            this._sharedUtilsService.openSnackbar(
              'To miejsce znajduje się zbyt blisko innego parkingu.',
              'ERROR',
            );
            return EMPTY;
          } else if (!this._mapService.isMarkerInRadiusOfOriginalParking()) {
            this._sharedUtilsService.openSnackbar(
              `Punkt znajduje się zbyt daleko od oryginału.`,
              'ERROR',
            );
            return EMPTY;
          }

          const selectedPoi: ParkingPoint | null = this._selectedParking();
          const location = this._mapService.getMarkerLatLng();

          return this._parkingEditLocationApiService
            .addEditLocationProposal(selectedPoi!.id, {
              location,
            })
            .pipe(
              tap(() => {
                this._sharedUtilsService.openSnackbar(
                  'Gotowe!\nWysłano propozycję zmiany lokalizacji parkingu.',
                  'SUCCESS',
                );
              }),
              catchError(() => {
                this._sharedUtilsService.openSnackbar(
                  'Wystąpił błąd podczas aktualizacji pozycji parkingu.',
                  'ERROR',
                );
                return of(null);
              }),
            );
        } else {
          return of(null);
        }
      }),
    );
  }

  async startAddingPoi() {
    this._mapService.renderMoveableMarkerWithRadiusAndLineToFixedPoint();
    const sheetRef = await this._sharedUtilsService.openMenuSheet(addingPoiConfirmSheetConfig(), {
      hasBackdrop: false,
    });

    return sheetRef.onDismiss.pipe(
      switchMap((result) => {
        if (result === MenuSheetResult.CONFIRM) {
          if (this._mapService.isMarkerInRadiusOfOtherParking()) {
            this._sharedUtilsService.openSnackbar(
              'To miejsce znajduje się zbyt blisko innego parkingu.',
              'ERROR',
            );
            return EMPTY;
          }

          const location = this._mapService.getMarkerLatLng();
          let reviewData: ReviewSaveData;
          let newParking: ParkingPoint;

          return from(import('../_components/add-review/add-review.component')).pipe(
            switchMap(({ AddReviewComponent }) =>
              this._matDialog
                .open(AddReviewComponent, {
                  data: {
                    parkingPointId: 0,
                    skipVoteStep: true,
                  },
                })
                .afterClosed(),
            ),
            filter((reviewData): reviewData is ReviewSaveData => !!reviewData),
            tap((data) => (reviewData = data)),
            switchMap(() => this._geocodeApiService.getAddressByCoordinates(location)),
            switchMap((address) =>
              this._parkingsApiService.postParking({ location, address: address || null }),
            ),
            tap((createdParking) => (newParking = createdParking)),
            switchMap(() => this._reviewsApiService.postReview(newParking.id, reviewData)),
            tap(() => {
              this._sharedUtilsService.openSnackbar(
                'Nowy darmowy parking został dodany.\nDzięki :)',
                'SUCCESS',
              );
              this._mapService.removeMoveableMarker();
              this._selectedParkingId.set(newParking.id);
              sheetRef.dismiss();
            }),
            catchError(() => {
              this._sharedUtilsService.openSnackbar(
                'Wystąpił błąd podczas dodawania parkingu.',
                'ERROR',
              );
              return of(null);
            }),
          );
        } else {
          sheetRef.dismiss();
          this._mapService.removeMoveableMarker();
          return of(null);
        }
      }),
      takeUntilDestroyed(this._destroyRef),
    );
  }
}
